import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AddStoreModal from '../modals/AddStoreModal';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Store } from '../types';

const Stores = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stores, setStores] = useState<Store[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [autoExtractFilter, setAutoExtractFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchStores = async (search: string = '', page: number = 1, autoExtract: string = '') => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('search', search);
      if (search) query.append('sort', 'ascending');
      if (autoExtract) query.append('enable_auto_extract', autoExtract);
      query.append('page', String(page));
      const url = `/stores?${query.toString()}`;

      const res = await fetchWithAuth(url);
      if (!res.ok) throw new Error('Failed to fetch stores');
      const data = await res.json();
      setStores(data.stores);
      setTotalPages(data.total_pages);
      setCurrentPage(data.current_page);
      setError(null);
    } catch (err) {
      setError('Something went wrong while fetching stores.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const page = isNaN(initialPage) || initialPage < 1 ? 1 : initialPage;
    fetchStores('', page, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchParams(prev => { prev.set('page', '1'); return prev; });
    fetchStores(searchTerm, 1, autoExtractFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExtractFilter]);

  const handleEdit = (store: Store) => {
    setSelectedStore(store);
    setIsModalOpen(true);
  };

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Stores</h1>
        <button
          onClick={() => {
            setSelectedStore(null);
            setIsModalOpen(true);
          }}
          className="w-full sm:w-auto bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
        >
          Add Store
        </button>
      </div>
      {error && <div className="text-red-600 font-medium mb-4">{error}</div>}

      <div className="mb-4">
        <div className="flex flex-col md:flex-row items-stretch md:items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Search</label>
            <input
              type="text"
              placeholder="Search by Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchParams(prev => { prev.set('page', '1'); return prev; });
                  fetchStores(searchTerm, 1, autoExtractFilter);
                }
              }}
              className="border px-4 py-2 rounded-md w-full md:w-80"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Auto Extract</label>
            <select
              value={autoExtractFilter}
              onChange={(e) => setAutoExtractFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All Stores</option>
              <option value="true">Auto Extract Enabled</option>
              <option value="false">Auto Extract Disabled</option>
            </select>
          </div>
          <button
            onClick={() => {
              setSearchParams(prev => { prev.set('page', '1'); return prev; });
              fetchStores(searchTerm, 1, autoExtractFilter);
            }}
            disabled={!searchTerm}
            className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Search
          </button>
          {(searchTerm || autoExtractFilter) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setAutoExtractFilter('');
                setSearchParams(prev => { prev.set('page', '1'); return prev; });
                fetchStores('', 1, '');
              }}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-400"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}
      <div className="overflow-x-auto min-h-[200px] pb-12">
        <table className="min-w-full bg-white border border-gray-200 rounded-md shadow-sm">
          <thead>
            <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
              <th className="px-2 md:px-4 py-1">ID</th>
              <th className="hidden md:table-cell px-4 py-1">Created At</th>
              <th className="px-2 md:px-4 py-2">Name</th>
              <th className="hidden md:table-cell px-4 py-1">Domains</th>
              <th className="hidden md:table-cell px-4 py-1">Auto Extract</th>
              <th className="px-2 md:px-4 py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <tr
                key={store.id}
                className={`border-t border-gray-100 text-sm text-gray-800 hover:bg-yellow-100 ${
                  openActionMenuId === store.id ? 'bg-yellow-100' : ''
                }`}
              >
                <td className="px-2 md:px-4 py-1">{store.id}</td>
                <td className="hidden md:table-cell px-4 py-1">
                  {new Date(store.created_at).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  })}
                </td>
                <td className="px-2 md:px-4 py-2">{store.name}</td>
                <td className="hidden md:table-cell px-4 py-1 text-gray-500">
                  {store.store_domains && store.store_domains.length > 0
                    ? store.store_domains.map((d) => d.domain).join(', ')
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="hidden md:table-cell px-4 py-1">
                  <span className={`inline-block w-3 h-3 rounded-full ${store.enable_auto_extract ? 'bg-green-500' : 'bg-red-500'}`} />
                </td>
                <td className="px-2 md:px-4 py-1">
                  <div className="relative inline-block text-left">
                    <button
                      onClick={() => setOpenActionMenuId(openActionMenuId === store.id ? null : store.id)}
                      className="p-1 rounded hover:bg-gray-100"
                    >
                      <span className="text-xl">⋯</span>
                    </button>
                    {openActionMenuId === store.id && (
                      <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded shadow-md z-10">
                        <button
                          onClick={() => {
                            handleEdit(store);
                            setOpenActionMenuId(null);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-center items-center space-x-4 mt-4">
          <button
            disabled={currentPage === 1}
            onClick={() => {
              const newPage = currentPage - 1;
              setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
              fetchStores(searchTerm, newPage, autoExtractFilter);
            }}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => {
              const newPage = currentPage + 1;
              setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
              fetchStores(searchTerm, newPage, autoExtractFilter);
            }}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
      <AddStoreModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedStore(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedStore(null);
          fetchStores(searchTerm, currentPage, autoExtractFilter);
        }}
        selectedStore={selectedStore} // Pass selected store to modal
      />
    </div>
  );
};

export default Stores;
